import React, { useState } from 'react';

import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

import './App.css';

import { AuthProvider, useAuth } from './contexts/AuthContext';

import { LocalizationProvider } from '@mui/x-date-pickers';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import Login from './components/Login';

import CompanySignup from './components/CompanySignup';

import LaunchPage from './components/LaunchPage';

import SafeLandingPage from './components/SafeLandingPage';

import Diagnostics from './components/Diagnostics';

import DashboardOverview from './components/DashboardOverview';

import ProfessionalLayout from './components/ProfessionalLayout';



import FacilityDashboard from './components/FacilityDashboard';

import ProductDashboard from './components/ProductDashboard';

import ICSRDashboard from './components/ICSRDashboard';

import SafetyDocumentDashboard from './components/SafetyDocumentDashboard';

import EnterpriseDashboard from './components/EnterpriseDashboard';

import UserManagement from './components/UserManagement';

import AIComplianceAssistant from './components/AIComplianceAssistant';


import WorkflowDashboard from './components/WorkflowDashboard';

import GMPCompliance from './components/GMPCompliance'; // Production build fix

import RegulatorySubmissions from './components/RegulatorySubmissions';


import PrivacyPolicy from './components/PrivacyPolicy';

import TermsOfService from './components/TermsOfService';

import DisclaimerSecurity from './components/DisclaimerSecurity';
import Integrations from './components/Integrations';
import Security from './components/Security';
import Blog from './components/Blog';
import Careers from './components/Careers';
import HelpCenter from './components/HelpCenter';
import Docs from './components/Docs';
import StatusPage from './components/StatusPage';
import Api from './components/Api';
import Partners from './components/Partners';

import LiteratureReviewEnhanced from './components/LiteratureReviewEnhanced';
import TRAHome from './components/TRAHome';
import DuplicateDetection from './components/DuplicateDetection';

import { SnackbarProvider } from './hooks/useSnackbar';
import SEO from './components/SEO';
import MoCRA from './components/MoCRA';



function AppContent() {

  const { isAuthenticated } = useAuth();

  const navigate = useNavigate();

  const location = useLocation();

  const [activeTab, setActiveTab] = useState<string>(() => {

    const params = new URLSearchParams(location.search);

    return params.get('tab') || 'dashboard';

  });



  // Keep activeTab in sync with URL query param (?tab=...)

  React.useEffect(() => {

    const params = new URLSearchParams(location.search);

    const urlTab = params.get('tab');

    if (urlTab && urlTab !== activeTab) {

      setActiveTab(urlTab);

    }

  }, [location.search, activeTab]);



  // Redirect to login if not authenticated

  React.useEffect(() => {

    if (!isAuthenticated) {

      navigate('/login');

    }

  }, [isAuthenticated, navigate]);



  // Show loading or redirect if not authenticated

  if (!isAuthenticated) {

    return <div className="min-h-screen flex items-center justify-center bg-gray-50">

      <div className="text-center">

        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>

        <p className="mt-4 text-gray-600">Loading...</p>

      </div>

    </div>;

  }



  const renderContent = () => {

    switch (activeTab) {

      case 'dashboard': return <DashboardOverview />;

      case 'facilities': return <FacilityDashboard />;

      case 'products': return <ProductDashboard />;

      case 'adverse-events': return <ICSRDashboard />;

      case 'icsr': return <ICSRDashboard />;

      case 'safety-documents': return <SafetyDocumentDashboard />;

      case 'workflow': return <WorkflowDashboard />;

      case 'gmp-compliance': return <GMPCompliance />;

      case 'submissions': return <RegulatorySubmissions />;


      case 'enterprise': return <EnterpriseDashboard />;

      case 'user-management': return <UserManagement />;

      case 'ai-assistant': return <AIComplianceAssistant />;


      case 'literature-review': return <LiteratureReviewEnhanced />;

      case 'tra': return <TRAHome />;

      case 'duplicate-detection': return <DuplicateDetection />;

      default: return <DashboardOverview />;

    }

  };



  const handleTabChange = (tab: string) => {

    setActiveTab(tab);

    const params = new URLSearchParams(location.search);

    params.set('tab', tab);

    navigate(`/dashboard?${params.toString()}`, { replace: true });

  };



  return (

    <ProfessionalLayout activeTab={activeTab} onTabChange={handleTabChange}>

      {renderContent()}

    </ProfessionalLayout>

  );

}



function App() {

  return (

    <Router>

      <AuthProvider>

        <SnackbarProvider>

          <LocalizationProvider dateAdapter={AdapterDateFns}>

            <Routes>

          <Route path="/" element={<>
            <SEO
              title="CosmaComply • MoCRA Compliance Software for Cosmetic Manufacturers"
              description="AI-assisted MoCRA compliance platform for cosmetic manufacturers. Streamline workflows, track deadlines, and support FDA reporting. Not affiliated with or approved by the FDA."
              canonical="https://cosmacomply.vercel.app/"
              keywords={[
                'MoCRA',
                'Modernization of Cosmetics Regulation Act',
                'cosmetics compliance software',
                'cosmetics regulatory compliance',
                'cosmetovigilance',
                'adverse event reporting',
                'cosmetic product listing',
                'facility registration',
                'TRA',
                'toxicological risk assessment'
              ]}
              jsonLd={{
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: [
                  { '@type': 'Question', name: 'What is MoCRA?', acceptedAnswer: { '@type': 'Answer', text: 'MoCRA is the Modernization of Cosmetics Regulation Act in the United States. It updates FDA oversight of cosmetics including registration, listing, safety substantiation, and adverse event reporting.' } },
                  { '@type': 'Question', name: 'Who must comply with MoCRA?', acceptedAnswer: { '@type': 'Answer', text: 'Most cosmetics manufacturers and responsible persons marketing cosmetics in the U.S. must comply. Certain small business exemptions may apply in specific areas.' } },
                  { '@type': 'Question', name: 'What does facility registration involve?', acceptedAnswer: { '@type': 'Answer', text: 'Facilities manufacturing or processing cosmetics must register with the FDA and renew periodically. Maintaining accurate records and renewals is essential.' } },
                  { '@type': 'Question', name: 'What is cosmetic product listing?', acceptedAnswer: { '@type': 'Answer', text: 'Responsible persons must submit product listing information to FDA for marketed cosmetic products and keep listings updated.' } },
                  { '@type': 'Question', name: 'How are adverse events handled?', acceptedAnswer: { '@type': 'Answer', text: 'Potential serious adverse events should be evaluated, and reportable cases must be submitted to FDA within specified timelines with appropriate follow-up.' } },
                  { '@type': 'Question', name: 'Does CosmaComply automate TRA and safety dossiers?', acceptedAnswer: { '@type': 'Answer', text: 'Yes—CosmaComply helps calculate SED/MoS and assemble safety substantiation documents, generating professional PDFs for review. CosmaComply is not affiliated with or approved by the FDA.' } }
                ]
              }}
            />
            <SafeLandingPage />
          </>} />

          <Route path="/launch" element={<>
            <SEO
              title="Launch • CosmaComply"
              description="Get started with CosmaComply’s MoCRA compliance platform."
              canonical="https://cosmacomply.vercel.app/launch"
            />
            <LaunchPage />
          </>} />

          <Route path="/signup" element={<>
            <SEO title="Sign up • CosmaComply" noindex />
            <CompanySignup />
          </>} />

          <Route path="/login" element={<>
            <SEO title="Login • CosmaComply" noindex />
            <Login />
          </>} />

          <Route path="/privacy" element={<>
            <SEO
              title="Privacy Policy • CosmaComply"
              description="Read CosmaComply’s privacy practices and data handling policies."
              canonical="https://cosmacomply.vercel.app/privacy"
            />
            <PrivacyPolicy />
          </>} />

          <Route path="/terms" element={<>
            <SEO
              title="Terms of Service • CosmaComply"
              description="Review CosmaComply’s terms and conditions."
              canonical="https://cosmacomply.vercel.app/terms"
            />
            <TermsOfService />
          </>} />

          <Route path="/disclaimer" element={<>
            <SEO
              title="Disclaimer & Security • CosmaComply"
              description="Important disclaimers and security notes about CosmaComply."
              canonical="https://cosmacomply.vercel.app/disclaimer"
            />
            <DisclaimerSecurity />
          </>} />
          <Route path="/integrations" element={<>
            <SEO title="Integrations • CosmaComply" canonical="https://cosmacomply.vercel.app/integrations" />
            <Integrations />
          </>} />
          <Route path="/mocra" element={<>
            <SEO
              title="MoCRA Compliance for Cosmetics • CosmaComply"
              description="Understand MoCRA requirements and how CosmaComply supports facility registration, cosmetic product listing, adverse event reporting, and safety substantiation with TRA."
              canonical="https://cosmacomply.vercel.app/mocra"
              keywords={[
                'MoCRA',
                'Modernization of Cosmetics Regulation Act',
                'cosmetics compliance',
                'cosmetic product listing',
                'facility registration',
                'cosmetovigilance',
                'TRA',
                'toxicological risk assessment'
              ]}
            />
            <MoCRA />
          </>} />
          <Route path="/security" element={<>
            <SEO title="Security • CosmaComply" canonical="https://cosmacomply.vercel.app/security" />
            <Security />
          </>} />
          <Route path="/blog" element={<>
            <SEO title="Blog • CosmaComply" canonical="https://cosmacomply.vercel.app/blog" />
            <Blog />
          </>} />
          <Route path="/careers" element={<>
            <SEO title="Careers • CosmaComply" canonical="https://cosmacomply.vercel.app/careers" />
            <Careers />
          </>} />
          <Route path="/help" element={<>
            <SEO title="Help Center • CosmaComply" canonical="https://cosmacomply.vercel.app/help" />
            <HelpCenter />
          </>} />
          <Route path="/docs" element={<>
            <SEO title="Documentation • CosmaComply" canonical="https://cosmacomply.vercel.app/docs" />
            <Docs />
          </>} />
          <Route path="/status" element={<>
            <SEO title="Status • CosmaComply" canonical="https://cosmacomply.vercel.app/status" />
            <StatusPage />
          </>} />
          <Route path="/api" element={<>
            <SEO title="API • CosmaComply" canonical="https://cosmacomply.vercel.app/api" />
            <Api />
          </>} />
          <Route path="/partners" element={<>
            <SEO title="Partners • CosmaComply" canonical="https://cosmacomply.vercel.app/partners" />
            <Partners />
          </>} />

          <Route path="/diagnostics" element={<>
            <SEO noindex />
            <Diagnostics />
          </>} />

          <Route path="/dashboard" element={<>
            <SEO noindex />
            <AppContent />
          </>} />

          <Route path="/dashboard/*" element={<>
            <SEO noindex />
            <AppContent />
          </>} />

          <Route path="*" element={<Navigate to="/" replace />} />

            </Routes>

          </LocalizationProvider>

        </SnackbarProvider>

      </AuthProvider>

    </Router>

  );

}



export default App;